import { useChatSidebarHandlers } from '@/app-shell/layout/Navigation/hooks/useChatSidebarHandlers';
import { useMobilePanel } from '@/app-shell/layout/Navigation/mobile/use-mobile-panel';
import { NavItem } from '@/app-shell/layout/Navigation/sidebar/nav-item';
import { cn } from '@/lib/cn';
import { getChatListVersion } from '@heidi/agents/contracts';
import { SidebarMenuButton, SidebarMenuItem } from '@heidi/design-system/page-shell';
import { typographyVariant } from '@heidi/design-system/typography';
import { useCallback, useState } from 'react';
import {
  AGENT_HOME_NAV_ROW,
  AGENT_HOME_THREAD_ROW_HOVER,
  AGENT_HOME_THREAD_ROW_TEXT,
  useIsAgentHomeNav,
} from '../agent-home-nav-context';
import { trackNavV3ButtonClicked } from '../nav-v3-analytics';
import { WorkSidebarApprovalHand } from '../work-sidebar-approval-hand';
import { ChatRowActionsMenu } from './chat-row-actions-menu';
import { ChatRowRenameInput } from './chat-row-rename-input';

interface RecentChatRowProps {
  chat: { id: string; title: string; version?: 1 | 2 };
  /** The existing project-attention signal: filled when `chat.id` (its
   *  project id) has a pending inbox item waiting on the user. */
  needsAttention?: boolean;
  /** True when this row is rendered inside the nav-v3 Pinned section rather
   *  than Recents — distinguishes the analytics label, and is the fast-path
   *  default for the row action menu's Pin/Unpin label. */
  isPinnedSection?: boolean;
}

/**
 * Recent-chat row with a hover/focus-revealed ellipsis action menu and the
 * same actions on right-click (Pin/Unpin, Rename, Copy, Delete — see
 * `ChatRowActionsMenu`). Deliberately NOT the quarantined `ChatSidebarItem`
 * (chat-domain imports are frozen); the row itself only navigates, v1 chats
 * to `/scribe/chat/[id]` and v2 Work chats to `/scribe/chat/projects/[id]`.
 */
export const RecentChatRow = ({
  chat,
  needsAttention = false,
  isPinnedSection = false,
}: RecentChatRowProps) => {
  const { currentChatId, handleSelectChat, handleDeleteChat } = useChatSidebarHandlers();
  const { dismissMobileSidebar } = useMobilePanel();
  const isAgentHomeNav = useIsAgentHomeNav();
  const [isRenaming, setIsRenaming] = useState(false);

  const onClick = useCallback(() => {
    trackNavV3ButtonClicked({
      label: isPinnedSection ? 'nav_v3_pinned_chat' : 'nav_v3_recent_chat',
      chat_id: chat.id,
    });
    handleSelectChat(chat.id, getChatListVersion(chat));
    dismissMobileSidebar();
  }, [handleSelectChat, chat, dismissMobileSidebar, isPinnedSection]);

  const handleDeleteSuccess = useCallback(() => {
    handleDeleteChat(chat.id);
  }, [handleDeleteChat, chat.id]);

  if (isRenaming) {
    // A plain `div` via `asChild`, not the row's usual literal `<button>` —
    // `ChatRowRenameInput`'s `<input>` may not validly nest inside one.
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={false}
          className={cn(
            isAgentHomeNav && typographyVariant({ variant: 'p2' }),
            isAgentHomeNav && AGENT_HOME_NAV_ROW
          )}
        >
          <div className="flex w-full items-center gap-1.5">
            <WorkSidebarApprovalHand needsAttention={needsAttention} />
            <ChatRowRenameInput
              chatId={chat.id}
              initialTitle={chat.title}
              onDone={() => setIsRenaming(false)}
            />
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <ChatRowActionsMenu
      chat={chat}
      isPinnedSection={isPinnedSection}
      onRenameStart={() => setIsRenaming(true)}
      onDeleteSuccess={handleDeleteSuccess}
      renderRow={({ action, contextItems }) => (
        <NavItem
          label={chat.title}
          icon={<WorkSidebarApprovalHand needsAttention={needsAttention} />}
          onClick={onClick}
          isActive={currentChatId === chat.id}
          testId="sidebar-link-recent-chat"
          className={cn(
            needsAttention ? 'text-text-primary' : AGENT_HOME_THREAD_ROW_TEXT,
            AGENT_HOME_THREAD_ROW_HOVER
          )}
          truncateLabel
          menuItemDataAttributes={{ 'data-recent-chat-id': chat.id }}
          action={action}
          rowContextMenu={contextItems}
        />
      )}
    />
  );
};
